<template>
  <!-- Print-only datasheet. The on-screen layout is hidden in print (see
       CarDetail/index.vue) and this block lays the same data out in the order
       the sales team prints it: IDNR + price, key figures, description,
       contact, images. -->
  <div class="car-detail-print hidden print:block" v-if="carDetail">
    <PageComponentsCarDetailTitle class="mb-4" />

    <!-- 1. IDNR + Preis -->
    <!-- The on-screen IDNR is a white-on-accent pill; in print the accent
         background is dropped, leaving the text invisible/grey — so render it
         as plain black text here. -->
    <div class="flex items-center justify-between gap-4 mb-8">
      <PageComponentsCarDetailID class="!bg-transparent !text-primary !px-0 !rounded-none" />
      <PageComponentsCarDetailPrice />
    </div>

    <!-- 2. Kacheln (Eckdaten) -->
    <PageComponentsCarDetailCarNumericals class="mb-8" />

    <!-- 3. Beschreibung / Ausstattung -->
    <div class="mb-8">
      <template v-for="tab in tabs" :key="tab.value">
        <div v-if="tab.content" class="mb-6 break-inside-avoid">
          <div class="inner-html-text text-regular-normal" v-html="tab.content"></div>
        </div>
      </template>
    </div>

    <!-- 4. Kontakt -->
    <!-- Plain <div>, not <section>: the page hides every <section> that does not
         itself contain .car-detail-container (see the [slug].vue print styles). -->
    <div v-if="dealerLocation" class="car-detail-print__contact mb-8 break-inside-avoid">
      <h5 class="h5 mb-4">{{ t('components.carDetail.print.contactTitle') }}</h5>
      <div class="flex flex-col gap-2 text-regular-normal">
        <p class="text-regular-semibold">{{ dealerLocation.shortName }}</p>
        <p>{{ dealerLocation.postalCode }} {{ dealerLocation.addressLocality }}, {{ dealerLocation.streetAddress }}</p>
        <p>
          <span class="text-regular-semibold">{{ t('components.carDetail.print.phone') }}:</span>
          {{ DEALER_TELEPHONE }}
        </p>
        <p>
          <span class="text-regular-semibold">{{ t('components.carDetail.print.email') }}:</span>
          {{ DEALER_EMAIL }}
        </p>
        <div>
          <p class="text-regular-semibold">{{ t('components.carDetail.print.openingHours') }}:</p>
          <table>
            <tbody>
              <tr v-for="(hours, i) in dealerLocation.openingHours" :key="i">
                <td class="pr-4 align-top">{{ hours.days.length > 1 ? t('components.carDetail.print.monFri') : t('components.carDetail.print.saturday') }}:</td>
                <td>{{ hours.opens }} - {{ hours.closes }} {{ t('components.carDetail.print.hoursSuffix') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 5. Bilder -->
    <div v-if="vehicleImages.length" class="car-detail-print__images grid grid-cols-2 gap-4">
      <SharedPicture
        v-for="(image, i) in vehicleImages"
        :key="image?.url || i"
        :media="image"
        fetchpriority="low"
        loading="lazy"
        imageClass="w-full h-full overflow-hidden !object-contain object-center rounded-sm"
        class="h-full !aspect-[303/228] w-full !object-contain object-center break-inside-avoid"
        height="483"
        width="642"
        format="webp"
        :quality="85"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { GraphQLCarDetail } from '~/types/graphql';
  import { DEALER_TELEPHONE, DEALER_EMAIL, resolveDealerLocation } from '#shared/dealer-locations';

  const { t } = useI18n();

  const carDetail = inject<GraphQLCarDetail>('carDetail');

  const { tabs } = useVehicleTextContent(carDetail);
  const vehicleImages = computed(() => useVehicleImages(carDetail ?? {}));
  const dealerLocation = computed(() => resolveDealerLocation(carDetail?.location));
</script>

<style lang="scss" scoped></style>
